import React from 'react';
import { MapPin, Waves, ShoppingBag, Coffee, Landmark, Camera } from 'lucide-react';

const DaysOut = () => {
  const attractions = [
    {
      icon: <Waves className="w-6 h-6" />,
      title: "Ayr Beach",
      distance: "5 minutes",
      description: "Beautiful sandy beach perfect for a walk before or after your session",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "Ayr Town Centre",
      distance: "Walking distance",
      description: "High street shopping, restaurants, and cafes in the heart of Ayr",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Coffee className="w-6 h-6" />,
      title: "Cafes & Restaurants",
      distance: "Walking distance",
      description: "Numerous family-friendly eateries nearby for lunch or refreshments",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <Landmark className="w-6 h-6" />,
      title: "Burns Cottage",
      distance: "15 minutes",
      description: "Birthplace of Robert Burns - Scotland's national poet",
      color: "from-green-500 to-teal-500"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Culzean Castle",
      distance: "20 minutes",
      description: "Stunning clifftop castle with gardens and country park",
      color: "from-indigo-500 to-purple-500"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Ayr Racecourse",
      distance: "5 minutes",
      description: "Scotland's premier horse racing venue with events throughout the year",
      color: "from-cyan-500 to-blue-500"
    }
  ];

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-full text-sm font-semibold mb-4">
            Make a Day of It
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[hsl(210,100%,20%)] mb-4">
            Things to Do in Ayr
          </h2>
          <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
            Kyle Drive is perfectly located in Ayr - make it a family day out with these nearby attractions
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {attractions.map((attraction, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-200 hover:border-cyan-400 hover:shadow-xl transition-all duration-300"
            >
              <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${attraction.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <div className="text-white">{attraction.icon}</div>
              </div>
              <h3 className="text-xl font-bold text-[hsl(210,100%,20%)] mb-2">
                {attraction.title}
              </h3>
              <div className="inline-block px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-3">
                {attraction.distance}
              </div>
              <p className="text-gray-700 text-sm">
                {attraction.description}
              </p>
            </div>
          ))}
        </div>

        {/* Plan Your Visit */}
        <div className="bg-gradient-to-r from-cyan-500 via-blue-500 to-teal-500 rounded-3xl p-8 md:p-12 text-white">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-4">Plan Your Visit</h3>
              <p className="text-lg mb-6">
                With Kyle Drive in the heart of Ayr, you can easily combine your driving session with a family day out exploring South Ayrshire's attractions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">✓</span>
                  <span>Sessions run Saturday & Sunday, 10am - 4pm</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">✓</span>
                  <span>Free parking at Kyle Rise Car Park</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">✓</span>
                  <span>Town centre amenities within walking distance</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-300 font-bold">✓</span>
                  <span>Perfect for combining with a beach trip or shopping</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/30">
              <h4 className="text-2xl font-bold mb-4">Suggested Itinerary</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-white/20 rounded-full px-3 py-1 text-sm font-bold">10:00</div>
                  <div className="text-sm">Kyle Drive session</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-white/20 rounded-full px-3 py-1 text-sm font-bold">11:00</div>
                  <div className="text-sm">Lunch in Ayr town centre</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-white/20 rounded-full px-3 py-1 text-sm font-bold">13:00</div>
                  <div className="text-sm">Beach walk or shopping</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-white/20 rounded-full px-3 py-1 text-sm font-bold">15:00</div>
                  <div className="text-sm">Visit Burns Cottage or Culzean Castle</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DaysOut;
